import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/** Minimum horizontal finger travel (px) towards the drawer's edge that closes the open drawer */
const SWIPE_CLOSE_DISTANCE = 60;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
    this.#bindBackdrop();
    this.#setupAnimatedElementListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindBackdrop();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('touchcancel', this.#onTouchCancel);
    this.#backdrop?.removeEventListener('click', this.#onBackdropClick);
    this.#backdrop = null;
  }

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /**
   * Listen for clicks on the backdrop element itself. A listener on the element (rather than only
   * the delegated one on the document) makes mobile browsers treat the dimmed area as a tap target,
   * so a tap there closes the drawer instead of being snapped to a nearby menu link.
   */
  #bindBackdrop() {
    const backdrop = this.refs.backdrop instanceof HTMLElement ? this.refs.backdrop : null;
    if (backdrop === this.#backdrop) return;

    this.#backdrop?.removeEventListener('click', this.#onBackdropClick);
    this.#backdrop = backdrop;
    this.#backdrop?.addEventListener('click', this.#onBackdropClick);
  }

  #onBackdropClick = () => {
    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Where a one-finger touch on the open drawer (or its backdrop) started
   * @type {{ x: number, y: number } | null}
   */
  #swipeStart = null;

  /**
   * Remember the start of a possible swipe-to-close gesture
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    this.#swipeStart = null;

    if (!this.refs.details.classList.contains('menu-open') || event.touches.length !== 1) return;
    // Submenus have their own back and close buttons
    if (this.refs.menuDrawer.classList.contains('menu-drawer--has-submenu-opened')) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.menu-drawer, .menu-drawer__backdrop')) return;
    // The picture cards row scrolls sideways, so a swipe there must keep scrolling it
    if (target.closest('.menu-drawer__featured-content-list, input, select, textarea')) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#swipeStart = { x: touch.clientX, y: touch.clientY };
  };

  /**
   * Close the drawer after a clear horizontal swipe towards the edge it slid in from
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;

    if (!start || !this.refs.details.classList.contains('menu-open')) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const towardsEdge = getComputedStyle(this).direction === 'rtl' ? deltaX : -deltaX;

    if (towardsEdge >= SWIPE_CLOSE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      this.close();
    }
  };

  #onTouchCancel = () => {
    this.#swipeStart = null;
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}